/**
 * agenda.js — Agenda frontend GoBooking
 *
 * Gère :
 *   - Système d'onglets (Catégorie / Lieu / Date)
 *   - Filtres dynamiques (chips catégories, chips lieux, jetons dates)
 *   - Rendu des grilles de cartes via agenda-card.js
 *   - Carte OpenStreetMap sticky (onglet Lieu) via Leaflet
 *
 * Données attendues dans window.goBookingAgenda :
 * {
 *   data: { animations, categories, lieux, dates },
 *   ajax_url, nonce, plugin_url
 * }
 */

import { renderCard3col, renderCard2col } from './agenda-card.js';
import ParticipantManager from './participant-manager.js';
import { initSessionPagination } from './session-pagination.js';

// ---------------------------------------------------------------------------
// Sélecteurs constants (correspondent aux IDs du template agenda.php)
// ---------------------------------------------------------------------------
const SEL = {
    root          : '#go-agenda',
    loader        : '#agenda-loader',
    // Onglets
    tabBtns       : '.agenda-tab-btn',
    panels        : '.agenda-tab-panel',
    // Catégorie
    catFilters    : '#agenda-category-filters',
    gridCat       : '#agenda-grid-categorie',
    emptyCat      : '#agenda-empty-categorie',
    // Lieu
    lieuFilters   : '#agenda-lieu-filters',
    gridLieu      : '#agenda-grid-lieu',
    emptyLieu     : '#agenda-empty-lieu',
    osmMap        : '#agenda-osm-map',
    // Date
    dateTokens    : '#agenda-date-tokens',
    gridDate      : '#agenda-grid-date',
    emptyDate     : '#agenda-empty-date',
};

// ---------------------------------------------------------------------------
// État global
// ---------------------------------------------------------------------------
const state = {
    data           : null,   // payload complet (animations, categories, lieux, villes, dates)
    activeTab      : 'categorie',
    activeCategories : [],   // [] = toutes
    activeLieux      : [],   // [] = tous (conservé pour compatibilité interne)
    activeVilles     : [],   // [] = toutes (filtres onglet Lieu)
    activeDates      : [],   // [] = toutes
    initiativeOnly   : false, // filtre global "Initiative des communes"
    map            : null,   // instance Leaflet
    mapMarkers     : [],     // markers Leaflet courants
    mapInitialized : false,
};

// ---------------------------------------------------------------------------
// Point d'entrée
// ---------------------------------------------------------------------------
document.addEventListener('DOMContentLoaded', () => {
    const root = document.querySelector(SEL.root);
    if (!root) return;

    // Données injectées par wp_localize_script
    const config = window.goBookingAgenda;
    if (!config || !config.data) {
        hideLoader(root);
        return;
    }

    state.data = config.data;

    // Onglet mémorisé (sessionStorage)
    const savedTab = sessionStorage.getItem('gobooking_agenda_tab');
    if (savedTab && ['categorie', 'lieu', 'date'].includes(savedTab)) {
        state.activeTab = savedTab;
    }

    setupTabs(root);
    setupInitiativeToggle(root);
    // Rendu de l'onglet actif au démarrage
    activateTab(root, state.activeTab);
    hideLoader(root);
});

// ---------------------------------------------------------------------------
// Filtre global Initiative des communes
// ---------------------------------------------------------------------------
function setupInitiativeToggle(root) {
    const btns = root.querySelectorAll('.agenda-initiative-toggle');
    if (!btns.length) return;
    btns.forEach(btn => {
        btn.addEventListener('click', () => {
            state.initiativeOnly = !state.initiativeOnly;
            // Synchronise l'état sur tous les boutons (1 par panneau)
            btns.forEach(b => {
                b.classList.toggle('is-active', state.initiativeOnly);
                b.setAttribute('aria-pressed', state.initiativeOnly ? 'true' : 'false');
            });
            // Rafraîchir l'onglet actif
            if (state.activeTab === 'categorie') refreshGridCategorie(root);
            if (state.activeTab === 'lieu')      refreshGridLieu(root);
            if (state.activeTab === 'date')      refreshGridDate(root);
        });
    });
}

// Applique le filtre initiative sur un tableau d'animations
function applyInitiativeFilter(animations) {
    if (!state.initiativeOnly) return animations;
    return animations.filter(a => a.initiative_commune === true);
}

// ---------------------------------------------------------------------------
// Onglets
// ---------------------------------------------------------------------------
function setupTabs(root) {
    root.querySelectorAll(SEL.tabBtns).forEach(btn => {
        btn.addEventListener('click', () => {
            const tab = btn.dataset.tab;
            if (tab === state.activeTab) return;
            state.activeTab = tab;
            sessionStorage.setItem('gobooking_agenda_tab', tab);
            activateTab(root, tab);
        });
    });
}

function activateTab(root, tabName) {
    // Mise à jour des boutons
    root.querySelectorAll(SEL.tabBtns).forEach(btn => {
        const isActive = btn.dataset.tab === tabName;
        btn.classList.toggle('is-active', isActive);
        btn.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });

    // Mise à jour des panneaux
    root.querySelectorAll(SEL.panels).forEach(panel => {
        const panelTab = panel.id.replace('agenda-panel-', '');
        const isActive = panelTab === tabName;
        panel.classList.toggle('is-active', isActive);
        if (isActive) {
            panel.removeAttribute('hidden');
        } else {
            panel.setAttribute('hidden', '');
        }
    });

    // Rendu initial de chaque onglet (une seule fois)
    if (tabName === 'categorie') renderCategorie(root);
    if (tabName === 'lieu')      renderLieu(root);
    if (tabName === 'date')      renderDate(root);
}

// ---------------------------------------------------------------------------
// Helpers DOM
// ---------------------------------------------------------------------------
function hideLoader(root) {
    const loader = root.querySelector(SEL.loader);
    if (loader) loader.classList.add('is-hidden');
}

function setGridContent(gridEl, emptyEl, html) {
    gridEl.innerHTML = html;
    emptyEl.hidden   = html.trim() !== '';
}

// Attache les événements sur les cartes après insertion dans le DOM
function attachCardEvents(container) {
    // Bouton "S'inscrire" → modale d'inscription
    container.querySelectorAll('.agenda-card__btn--inscrit').forEach(btn => {
        btn.addEventListener('click', e => {
            e.stopPropagation();
            const articleId = btn.closest('[data-animation-id]')?.dataset.animationId;
            if (articleId) openInscriptionModal(parseInt(articleId, 10));
        });
    });

    // Bouton "Détail de l'animation" → drawer détail
    container.querySelectorAll('.agenda-card__btn--detail').forEach(btn => {
        btn.addEventListener('click', e => {
            e.stopPropagation();
            const articleId = btn.closest('[data-animation-id]')?.dataset.animationId;
            if (articleId) openDetailModal(parseInt(articleId, 10));
        });
    });

    // Clic sur la carte (hors boutons) → drawer détail
    container.querySelectorAll('.agenda-card').forEach(card => {
        card.addEventListener('click', e => {
            if (e.target.closest('.agenda-card__btn')) return;
            const id = card.dataset.animationId;
            if (id) openDetailModal(parseInt(id, 10));
        });
    });
}

// Ouvre la vue détaillée d'une animation
function openDetailModal(animationId) {
    const anim = state.data?.animations?.find(a => a.id === animationId);
    document.dispatchEvent(new CustomEvent('gobooking:open-detail', {
        detail: { animationId, slug: anim?.slug || '' },
        bubbles: true,
    }));
}

// Ouvre la modale d'inscription (implémentation autonome, sans dépendance à calendar.js)
function openInscriptionModal(animationId) {
    if (typeof bootstrap === 'undefined') {
        console.error('Bootstrap non disponible');
        return;
    }
    const anim = state.data?.animations?.find(a => a.id === animationId);
    showAgendaSessionModal({
        id   : String(animationId),
        title: anim?.title || '',
        extendedProps: {
            places_disponibles         : anim?.places_disponibles ?? 0,
            lieu_depart                : anim?.lieu || '',
            disable_native_inscription : anim?.disable_native_inscription || false,
            external_inscription_url   : anim?.external_inscription_url || '',
            external_inscription_label : anim?.external_inscription_label || '',
            require_taille_cm          : anim?.require_taille_cm || false,
            max_inscriptions_user      : anim?.max_inscriptions_user || 2,
        },
    });
}

// ---------------------------------------------------------------------------
// =========================================================================
// ONGLET CATÉGORIE
// =========================================================================
// ---------------------------------------------------------------------------
let catFilterBuilt = false;

function renderCategorie(root) {
    const { categories, animations } = state.data;

    // Construire les filtres une seule fois
    if (!catFilterBuilt) {
        buildChipFilters(
            root.querySelector(SEL.catFilters),
            categories,
            state.activeCategories,
            chip => {
                toggleInArray(state.activeCategories, chip);
                refreshGridCategorie(root);
            },
            /* hasAll */ true,
            'categorie'
        );
        catFilterBuilt = true;
    }

    refreshGridCategorie(root);
}

function refreshGridCategorie(root) {
    const filtered = applyInitiativeFilter(
        filterByCategories(state.data.animations, state.activeCategories)
    );
    const grid     = root.querySelector(SEL.gridCat);
    const empty    = root.querySelector(SEL.emptyCat);
    const html     = filtered.map(a => renderCard3col(a)).join('');
    setGridContent(grid, empty, html);
    attachCardEvents(grid);
    syncChipState(root.querySelector(SEL.catFilters), state.activeCategories);
}

function filterByCategories(animations, activeCategories) {
    if (!activeCategories || activeCategories.length === 0) return animations;
    return animations.filter(a =>
        a.categories && a.categories.some(c => activeCategories.includes(c.name))
    );
}

// ---------------------------------------------------------------------------
// =========================================================================
// ONGLET LIEU
// =========================================================================
// ---------------------------------------------------------------------------
let lieuFilterBuilt = false;

function renderLieu(root) {
    const { villes } = state.data;

    if (!lieuFilterBuilt) {
        // Transformer villes (array de strings) en objets {name, color} pour buildChipFilters
        const villeItems = (villes || []).map(v => ({ name: v, color: '#6c757d' }));
        buildChipFilters(
            root.querySelector(SEL.lieuFilters),
            villeItems,
            state.activeVilles,
            chip => {
                toggleInArray(state.activeVilles, chip);
                refreshGridLieu(root);
            },
            /* hasAll */ true,
            'lieu'
        );
        lieuFilterBuilt = true;
    }

    refreshGridLieu(root);

    // Initialiser la carte Leaflet (lazy — la 1ère fois que l'onglet est ouvert)
    if (!state.mapInitialized) {
        // Délai pour s'assurer que le panneau est rendu et que le CSS de hauteur
        // est appliqué (critique sur mobile où la hauteur est fixée en CSS).
        setTimeout(() => initAgendaMap(root), 100);
    } else {
        // Si la carte est déjà initialisée, recalculer sa taille au cas où elle
        // aurait été masquée (retour à l'onglet Lieu sur mobile ou redimensionnement).
        setTimeout(() => state.map && state.map.invalidateSize(), 100);
    }
}

function refreshGridLieu(root) {
    const filtered = applyInitiativeFilter(
        filterByVilles(state.data.animations, state.activeVilles)
    );
    const grid     = root.querySelector(SEL.gridLieu);
    const empty    = root.querySelector(SEL.emptyLieu);
    const html     = filtered.map(a => renderCard2col(a)).join('');
    setGridContent(grid, empty, html);
    attachCardEvents(grid);
    syncChipState(root.querySelector(SEL.lieuFilters), state.activeVilles);

    // MAJ des marqueurs de la carte si elle est initialisée
    if (state.mapInitialized) updateMapMarkers(filtered, state.activeVilles);
}

function filterByVilles(animations, activeVilles) {
    if (!activeVilles || activeVilles.length === 0) return animations;
    return animations.filter(a =>
        a.villes && a.villes.some(v => activeVilles.includes(v))
    );
}

// ---- Carte OSM (Leaflet) --------------------------------------------------

function initAgendaMap(root) {
    const mapEl = root.querySelector(SEL.osmMap);
    if (!mapEl) return;

    // Vérifier que Leaflet est disponible
    if (typeof window.L === 'undefined') return;
    const L = window.L;

    // Éviter double-init
    if (mapEl._leaflet_id) return;

    state.map = L.map(mapEl, { zoomControl: true, scrollWheelZoom: false });

    L.tileLayer('https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png', {
        attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
        subdomains  : 'abcd',
        maxZoom     : 19,
    }).addTo(state.map);

    state.mapInitialized = true;

    // Afficher tous les marqueurs initialement
    const filtered = filterByVilles(state.data.animations, state.activeVilles);
    updateMapMarkers(filtered, state.activeVilles);
}

function updateMapMarkers(animations, activeVilles = []) {
    if (!state.map || !state.mapInitialized) return;
    const L = window.L;

    state.mapMarkers.forEach(m => m.remove());
    state.mapMarkers = [];

    const entries = [];
    animations.forEach(anim => {
        // Pour la carte, utiliser map_sessions (toutes les sessions, sans filtre de date)
        // afin d'afficher tous les marqueurs, comme la carte du détail d'une animation.
        // Fallback sur sessions si map_sessions n'est pas disponible.
        const sessionsForMap = anim.map_sessions && anim.map_sessions.length > 0
            ? anim.map_sessions
            : (anim.sessions || []);
        const hasSessions = sessionsForMap.length > 0;

        if (hasSessions) {
            const locationGroups = {};
            sessionsForMap.forEach(session => {
                const lat = parseFloat(session.latitude);
                const lng = parseFloat(session.longitude);
                if (isNaN(lat) || isNaN(lng) || (lat === 0 && lng === 0)) return;
                if (activeVilles.length > 0 &&
                    (!session.ville || !activeVilles.includes(session.ville))) return;
                const key = lat.toFixed(6) + ',' + lng.toFixed(6);
                if (!locationGroups[key]) {
                    locationGroups[key] = { lat, lng, sessions: [] };
                }
                locationGroups[key].sessions.push(session);
            });

            const groups = Object.values(locationGroups);
            if (groups.length > 0) {
                groups.forEach(group => {
                    entries.push({ lat: group.lat, lng: group.lng, anim, sessions: group.sessions });
                });
            } else {
                const lat = parseFloat(anim.latitude);
                const lng = parseFloat(anim.longitude);
                if (!isNaN(lat) && !isNaN(lng) && !(lat === 0 && lng === 0)) {
                    entries.push({ lat, lng, anim, sessions: [] });
                }
            }
        } else {
            const lat = parseFloat(anim.latitude);
            const lng = parseFloat(anim.longitude);
            if (!isNaN(lat) && !isNaN(lng) && !(lat === 0 && lng === 0)) {
                entries.push({ lat, lng, anim, sessions: [] });
            }
        }
    });

    // 2. Étaler en cercle les marqueurs qui partagent exactement le même point
    const spread = spreadOverlappingMarkers(entries);

    const bounds = [];
    spread.forEach(({ lat, lng, origLat, origLng, anim, sessions }) => {
        const color  = anim.categories?.[0]?.color || '#4ECDC4';
        const icon   = createColoredMarker(L, color);
        const marker = L.marker([lat, lng], { icon });
        marker.bindPopup(buildMapPopup(anim, sessions), { maxWidth: 264, minWidth: 240 });
        marker.addTo(state.map);
        state.mapMarkers.push(marker);

        // Rendre la carte popup cliquable pour ouvrir la modale de détail
        // (mêmes critères que la liste de l'agenda). Les zones interactives
        // internes (boutons, liens, badges) restent prioritaires.
        marker.on('popupopen', (e) => {
            const popupEl = e.popup.getElement();
            if (!popupEl) return;
            const card = popupEl.querySelector('.agenda-map-popup--clickable');
            if (!card) return;
            const triggerDetail = (ev) => {
                if (ev.target.closest('.agenda-map-popup__btn, .agenda-map-popup__libre, .agenda-map-popup__complet, a, button')) return;
                ev.preventDefault();
                state.map.closePopup();
                openDetailModal(anim.id);
            };
            card.addEventListener('click', triggerDetail);
            card.addEventListener('keydown', (ev) => {
                if (ev.key === 'Enter' || ev.key === ' ') triggerDetail(ev);
            });
        });

        // fitBounds sur la position originale (non décalée)
        bounds.push([origLat, origLng]);
    });

    // 3. Ajuster la vue
    if (bounds.length > 0) {
        if (bounds.length === 1) {
            state.map.setView(bounds[0], 13);
        } else {
            state.map.fitBounds(bounds, { padding: [32, 32] });
        }
    } else {
        // Vue par défaut (France)
        state.map.setView([46.85, 2.35], 6);
    }

    // Forcer le recalcul de taille si le panel était caché
    state.map.invalidateSize();
}

/**
 * Distribue en éventail les marqueurs d'animations qui partagent exactement les mêmes coordonnées.
 * Les marqueurs isolés ne sont pas modifiés.
 * Pour N animations superposées, elles sont réparties régulièrement sur un cercle
 * de rayon ~20 m (≈ 0.00018°), lisible à tous les niveaux de zoom pertinents.
 *
 * @param  {Array<{lat, lng, anim}>} entries
 * @returns {Array<{lat, lng, origLat, origLng, anim}>}
 */
function spreadOverlappingMarkers(entries) {
    // Regrouper par coordonnée exacte
    const groups = {};
    entries.forEach(entry => {
        const key = `${entry.lat},${entry.lng}`;
        (groups[key] = groups[key] || []).push(entry);
    });

    const result = [];
    Object.values(groups).forEach(group => {
        const N = group.length;

        if (N === 1) {
            result.push({ ...group[0], origLat: group[0].lat, origLng: group[0].lng });
            return;
        }

        // Rayon de base ≈ 20 m en degrés, augmenté légèrement au-delà de 6 points
        const R = 0.00018 * (1 + Math.floor(N / 7) * 0.6);

        group.forEach((entry, i) => {
            // On démarre à midi (−π/2) et on tourne dans le sens horaire
            const angle = (2 * Math.PI * i / N) - (Math.PI / 2);
            result.push({
                ...entry,
                origLat: entry.lat,
                origLng: entry.lng,
                lat: entry.lat + R * Math.cos(angle),
                lng: entry.lng + R * Math.sin(angle),
            });
        });
    });

    return result;
}

function buildMapPopup(anim, sessions = []) {
    const title    = escHtml(anim.title);
    const rawTitle = anim.title || '';
    let dateStr    = '';
    let lieuStr    = '';

    if (sessions.length === 1) {
        // Session unique : détecte les sessions multi-jours (date_fin ≠ date)
        const session    = sessions[0];
        const dateFin    = session.date_fin;
        const isMultiDay = dateFin && dateFin !== session.date && dateFin > session.date;
        if (isMultiDay) {
            const d1 = new Date(session.date   + 'T00:00:00');
            const d2 = new Date(dateFin        + 'T00:00:00');
            const l1 = isNaN(d1) ? session.date : d1.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
            const l2 = isNaN(d2) ? dateFin      : d2.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
            dateStr = `Du ${l1} au ${l2}`;
        } else {
            const d = new Date(session.date + 'T00:00:00');
            const dateLabel = isNaN(d) ? session.date
                : d.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
            const hd = session.heure_debut ? String(session.heure_debut).slice(0, 5) : '';
            const hf = session.heure_fin   ? String(session.heure_fin).slice(0, 5)   : '';
            dateStr = hd ? `${dateLabel} · ${hd}${hf ? ` - ${hf}` : ''}` : dateLabel;
        }

        const lieuParts = [session.lieu_depart, session.adresse, session.ville]
            .map(v => (v || '').trim()).filter(Boolean);
        lieuStr = escHtml(lieuParts.join(', '));
    } else if (sessions.length > 1) {
        // Plusieurs sessions au même lieu : liste compacte des dates
        const first = sessions[0];
        const lieuParts = [first.lieu_depart, first.adresse, first.ville]
            .map(v => (v || '').trim()).filter(Boolean);
        lieuStr = escHtml(lieuParts.join(', '));
        const dateLabels = sessions.map(s => {
            const d = new Date(s.date + 'T00:00:00');
            const dateLabel = isNaN(d) ? s.date
                : d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' });
            const hd = s.heure_debut ? String(s.heure_debut).slice(0, 5) : '';
            return hd ? `${dateLabel} · ${hd}` : dateLabel;
        });
        dateStr = `${sessions.length} sessions : ${dateLabels.join(' — ')}`;
    } else {
        // Fallback : 1ère date et lieu global de l'animation (pas de sessions en base)
        if (anim.dates && anim.dates.length > 0) {
            const d = new Date(anim.dates[0] + 'T00:00:00');
            dateStr = isNaN(d) ? anim.dates[0]
                : d.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
        }
        lieuStr = escHtml(anim.lieu || '');
    }

    // Même logique d'affichage des boutons que buildInscriptionBtn (agenda-card.js) :
    // Vérification au niveau animation (pas au niveau session, car map_sessions
    // ne contient pas les champs d'inscription).
    // 1. Inscription externe (vert) — lien vers site tiers
    // 2. Accès libre — aucune inscription nécessaire
    // 3. Complet (places == 0)
    // 4. Inscription native (bleu)
    const complet = anim.places_disponibles === 0;

    let btnHtml;
    if (anim.disable_native_inscription && anim.external_inscription_url) {
        const extUrl   = escHtml(anim.external_inscription_url);
        const extLabel = escHtml(anim.external_inscription_label || 'S\'inscrire');
        btnHtml = `<a class="agenda-map-popup__btn agenda-map-popup__btn--external"
               href="${extUrl}" target="_blank" rel="noopener noreferrer"
           >${extLabel}</a>`;
    } else if (anim.inscription_libre) {
        btnHtml = `<span class="agenda-map-popup__libre">Accès libre</span>`;
    } else if (complet) {
        btnHtml = `<span class="agenda-map-popup__complet">Complet</span>`;
    } else {
        btnHtml = `<button class="agenda-map-popup__btn agenda-map-popup__btn--inscrit" type="button"
               data-animation-id="${anim.id}"
               onclick="goBooking_popupInscription(${anim.id})"
           >S'inscrire</button>`;
    }

    // Thumbnail ou placeholder avec initiale de la catégorie
    const catColor  = String(anim.categories?.[0]?.color || '#4ECDC4').replace(/[^a-zA-Z0-9#(),%. ]/g, '');
    const initial   = rawTitle.trim().charAt(0).toUpperCase() || '?';
    const thumbHtml = anim.featured_image
        ? `<img class="agenda-map-popup__img" src="${escHtml(anim.featured_image)}" alt="" loading="lazy">`
        : `<div class="agenda-map-popup__placeholder" style="--cat-color:${catColor}"><span class="agenda-map-popup__initial">${escHtml(initial)}</span></div>`;

    // Icônes SVG Bootstrap Icons (calendar3 + geo-alt-fill)
    const calIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" fill="currentColor" viewBox="0 0 16 16" aria-hidden="true"><path d="M14 0H2a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V2a2 2 0 0 0-2-2zm1 13a1 1 0 0 1-1 1H2a1 1 0 0 1-1-1V5h14zM11 6.5v1a.5.5 0 0 1-1 0v-1a.5.5 0 0 1 1 0zm-3 0v1a.5.5 0 0 1-1 0v-1a.5.5 0 0 1 1 0zm-3 0v1a.5.5 0 0 1-1 0v-1a.5.5 0 0 1 1 0z"/></svg>`;
    const pinIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" fill="currentColor" viewBox="0 0 16 16" aria-hidden="true"><path d="M8 16s6-5.686 6-10A6 6 0 0 0 2 6c0 4.314 6 10 6 10zm0-7a3 3 0 1 1 0-6 3 3 0 0 1 0 6z"/></svg>`;

    return `
<div class="agenda-map-popup agenda-map-popup--clickable" data-animation-id="${anim.id}" role="button" tabindex="0">
    <div class="agenda-map-popup__thumb">${thumbHtml}</div>
    <div class="agenda-map-popup__body">
        <p class="agenda-map-popup__title">${title}</p>
        ${(dateStr || lieuStr) ? `<ul class="agenda-map-popup__meta">
            ${dateStr ? `<li>${calIcon}<span>${escHtml(dateStr)}</span></li>` : ''}
            ${lieuStr ? `<li>${pinIcon}<span>${lieuStr}</span></li>` : ''}
        </ul>` : ''}
    </div>
    <div class="agenda-map-popup__footer">${btnHtml}</div>
</div>`;
}

// Fonction globale appelée depuis les popups Leaflet (ne peut pas accéder à la closure)
window.goBooking_popupInscription = function (animationId /*, sessionId ignoré : openInscriptionModal charge les sessions */) {
    openInscriptionModal(animationId);
};

// Fonction globale appelée depuis animation-details.js (bouton "S'inscrire à l'animation")
window.goBookingOpenInscription = function (animId, animTitle, extendedProps) {
    if (typeof bootstrap === 'undefined') {
        console.error('Bootstrap non disponible');
        return;
    }
    showAgendaSessionModal({
        id           : String(animId),
        title        : animTitle || '',
        extendedProps: extendedProps || {},
    });
};

// Petit helper d'échappement HTML (sans import pour ce contexte)
function escHtml(str) {
    if (!str) return '';
    return String(str).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

// Crée un L.divIcon SVG pin coloré avec la couleur de catégorie
function createColoredMarker(L, color) {
    // Échappe manuellement la couleur (valeur hex/rgb contrôlée côté PHP, sécurité défensive)
    const safeColor = String(color).replace(/[^a-zA-Z0-9#(),%. ]/g, '');
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="28" height="38" viewBox="0 0 28 38">
        <path d="M14 1C6.82 1 1 6.82 1 14c0 9.8 13 23.5 13 23.5S27 23.8 27 14C27 6.82 21.18 1 14 1z"
              fill="${safeColor}" stroke="#fff" stroke-width="2"/>
        <circle cx="14" cy="14" r="5.5" fill="#fff" fill-opacity="0.9"/>
    </svg>`;
    return L.divIcon({
        html       : svg,
        className  : 'agenda-map-marker',  // reset les styles Leaflet par défaut
        iconSize   : [28, 38],
        iconAnchor : [14, 38],   // pointe du pin = ancre
        popupAnchor: [0, -40],   // popup au-dessus du pin
    });
}

// ---------------------------------------------------------------------------
// =========================================================================
// ONGLET DATE
// =========================================================================
// ---------------------------------------------------------------------------
let dateFilterBuilt = false;

function renderDate(root) {
    const { dates } = state.data;

    if (!dateFilterBuilt) {
        buildDateTokenFilters(
            root.querySelector(SEL.dateTokens),
            dates,
            state.activeDates,
            clicked => {
                toggleInArray(state.activeDates, clicked);
                refreshGridDate(root);
            }
        );
        dateFilterBuilt = true;
    }

    refreshGridDate(root);
}

function refreshGridDate(root) {
    const filtered = applyInitiativeFilter(
        filterByDates(state.data.animations, state.activeDates)
    );
    const grid     = root.querySelector(SEL.gridDate);
    const empty    = root.querySelector(SEL.emptyDate);
    const html     = filtered.map(a => renderCard3col(a)).join('');
    setGridContent(grid, empty, html);
    attachCardEvents(grid);
    syncTokenState(root.querySelector(SEL.dateTokens), state.activeDates);
}

function filterByDates(animations, activeDates) {
    if (!activeDates || activeDates.length === 0) return animations;
    return animations.filter(a => {
        // Sessions multi-jours : vérifier si une date sélectionnée tombe
        // dans la plage [date, date_fin] d'au moins une session.
        if (a.sessions && a.sessions.length > 0) {
            return a.sessions.some(s => {
                const end = s.date_fin || s.date;
                return activeDates.some(d => d >= s.date && d <= end);
            });
        }
        // Fallback : match exact sur le tableau de dates
        return a.dates && a.dates.some(d => activeDates.includes(d));
    });
}

// ---------------------------------------------------------------------------
// Constructeur de jetons de dates
// ---------------------------------------------------------------------------
const MONTH_NAMES_FR = ['', 'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
    'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];

function buildDateTokenFilters(container, dates, activeState, onToggle) {
    if (!container || !dates) return;
    container.innerHTML = '';

    // Jeton "Tout afficher"
    const allToken = document.createElement('button');
    allToken.type = 'button';
    allToken.className = 'agenda-date-token token-all' + (activeState.length === 0 ? ' is-active' : '');
    allToken.dataset.date = '__all__';
    allToken.setAttribute('aria-pressed', activeState.length === 0 ? 'true' : 'false');
    allToken.innerHTML = '<span class="token-num" style="font-size:.8125rem;">Tous</span>';
    allToken.addEventListener('click', () => {
        activeState.length = 0; // vider le filtre
        // re-render les tokens
        container.querySelectorAll('.agenda-date-token').forEach(el => {
            el.classList.toggle('is-active', el.dataset.date === '__all__');
            el.setAttribute('aria-pressed', el.dataset.date === '__all__' ? 'true' : 'false');
        });
        onToggle(null);
    });
    container.appendChild(allToken);

    // Grouper par mois/année puis créer label + tokens
    let currentMonthKey = null;

    dates.forEach(token => {
        const monthKey = `${token.year}-${token.month}`;

        // Insérer un séparateur de mois si on change de groupe
        if (monthKey !== currentMonthKey) {
            currentMonthKey = monthKey;
            const label = document.createElement('span');
            label.className = 'agenda-date-month-label';
            label.setAttribute('aria-hidden', 'true');
            label.textContent = MONTH_NAMES_FR[token.month] || '';
            container.appendChild(label);
        }

        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'agenda-date-token' + (activeState.includes(token.date) ? ' is-active' : '');
        btn.dataset.date = token.date;
        btn.setAttribute('aria-pressed', activeState.includes(token.date) ? 'true' : 'false');
        btn.setAttribute('aria-label', `Filtrer le ${token.day_initial} ${token.day_num}`);
        btn.innerHTML = `
            <span class="token-initial">${escHtml(token.day_initial)}</span>
            <span class="token-num">${token.day_num}</span>
        `;
        btn.addEventListener('click', () => onToggle(token.date));
        container.appendChild(btn);
    });
}

function syncTokenState(container, activeDates) {
    if (!container) return;
    const allIsActive = activeDates.length === 0;
    container.querySelectorAll('.agenda-date-token').forEach(el => {
        if (el.dataset.date === '__all__') {
            el.classList.toggle('is-active', allIsActive);
            el.setAttribute('aria-pressed', allIsActive ? 'true' : 'false');
        } else {
            const active = activeDates.includes(el.dataset.date);
            el.classList.toggle('is-active', active);
            el.setAttribute('aria-pressed', active ? 'true' : 'false');
        }
    });
}

// ---------------------------------------------------------------------------
// Constructeur de chips filtres (catégories / lieux)
// ---------------------------------------------------------------------------
function buildChipFilters(container, items, activeState, onToggle, hasAll, type) {
    if (!container || !items) return;
    container.innerHTML = '';

    if (hasAll) {
        const allBtn = document.createElement('button');
        allBtn.type = 'button';
        allBtn.className = 'agenda-chip' + (activeState.length === 0 ? ' is-active' : '');
        allBtn.dataset.value = '__all__';
        allBtn.setAttribute('aria-pressed', activeState.length === 0 ? 'true' : 'false');
        allBtn.textContent = 'Tout';
        allBtn.addEventListener('click', () => {
            activeState.length = 0;
            syncChipState(container, activeState);
            onToggle(null);
        });
        container.appendChild(allBtn);
    }

    items.forEach(item => {
        const name  = item.name;
        const color = item.color || '#4ECDC4';

        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'agenda-chip' + (activeState.includes(name) ? ' is-active' : '');
        btn.dataset.value = name;
        btn.setAttribute('aria-pressed', activeState.includes(name) ? 'true' : 'false');
        btn.style.setProperty('--chip-color', color);

        if (type === 'categorie') {
            // Puce colorée + libellé
            btn.innerHTML = `<span class="chip-dot" style="background:${escHtml(color)};"></span>${escHtml(name)}`;
        } else {
            btn.textContent = name;
        }

        btn.addEventListener('click', () => onToggle(name));
        container.appendChild(btn);
    });
}

function syncChipState(container, activeValues) {
    if (!container) return;
    const allIsActive = activeValues.length === 0;
    container.querySelectorAll('.agenda-chip').forEach(el => {
        if (el.dataset.value === '__all__') {
            el.classList.toggle('is-active', allIsActive);
            el.setAttribute('aria-pressed', allIsActive ? 'true' : 'false');
        } else {
            const active = activeValues.includes(el.dataset.value);
            el.classList.toggle('is-active', active);
            el.setAttribute('aria-pressed', active ? 'true' : 'false');
        }
    });
}

// ---------------------------------------------------------------------------
// Utilitaire : toggle d'une valeur dans un tableau (mute)
// ---------------------------------------------------------------------------
function toggleInArray(arr, value) {
    if (value === null) return; // « Tout » → reset géré en amont
    const idx = arr.indexOf(value);
    if (idx === -1) {
        arr.push(value);
    } else {
        arr.splice(idx, 1);
    }
}

// ---------------------------------------------------------------------------
// =========================================================================
// MODALE D'INSCRIPTION (auto-suffisante, sans dépendance à calendar.js)
// =========================================================================
// ---------------------------------------------------------------------------

/**
 * Affiche une notification Bootstrap temporaire.
 */
function showNotification(type, message) {
    const notification = document.createElement('div');
    notification.className = `alert alert-${type} notification`;
    notification.textContent = message;
    let container = document.querySelector('.notification-container');
    if (!container) {
        container = document.createElement('div');
        container.className = 'notification-container';
        Object.assign(container.style, {
            position : 'fixed',
            top      : '50%',
            left     : '50%',
            transform: 'translate(-50%, -50%)',
            zIndex   : '9999',
        });
        document.body.appendChild(container);
    }
    container.appendChild(notification);
    setTimeout(() => {
        notification.remove();
        if (!container.children.length) container.remove();
    }, 5000);
}

/**
 * Supprime toute modale d'inscription existante et ses résidus Bootstrap.
 */
function cleanupExistingModals() {
    const existing = document.getElementById('sessionSelectionModal');
    if (existing) {
        try {
            const bm = bootstrap.Modal.getInstance(existing);
            if (bm) bm.dispose();
            existing.remove();
        } catch (e) {}
    }
    document.querySelectorAll('.modal-backdrop').forEach(el => el.remove());
    document.body.classList.remove('modal-open');
    document.body.style.overflow     = '';
    document.body.style.paddingRight = '';
}

/**
 * Charge les sessions via AJAX puis ouvre la modale multi-étapes.
 */
async function showAgendaSessionModal(eventData) {
    const ajaxUrl = window.goBookingAgenda?.ajax_url;
    const nonce   = window.goBookingAgenda?.nonce;
    if (!ajaxUrl || !nonce) {
        console.error('goBookingAgenda non disponible');
        return;
    }
    try {
        const response = await fetch(ajaxUrl, {
            method : 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body   : new URLSearchParams({
                action      : 'gobooking_get_sessions',
                nonce,
                animation_id: eventData.id,
            }),
        });
        if (!response.ok) throw new Error('Erreur réseau');
        const result = await response.json();
        let availableSessions = [];
        if (result.success && result.data?.sessions) {
            availableSessions = result.data.sessions.filter(s => {
                const p = parseInt(s.places_disponibles);
                return p === -1 || p > 0;
            });
            if (result.data.sessions.length > 0 && result.data.sessions[0].require_taille_cm !== undefined) {
                eventData.extendedProps = eventData.extendedProps || {};
                eventData.extendedProps.require_taille_cm = result.data.sessions[0].require_taille_cm === true;
            }
        }
        buildInscriptionModal(eventData, availableSessions);
    } catch (err) {
        console.error('Erreur chargement sessions :', err);
        alert('Impossible de charger les sessions. Veuillez réessayer.');
    }
}

/**
 * Construit et affiche la modale d'inscription multi-étapes.
 */
function buildInscriptionModal(eventData, availableSessions) {
    cleanupExistingModals();

    // Map activite_titre → activite_description
    const activiteMap = {};
    availableSessions.forEach(s => {
        if (s.activite_titre) activiteMap[s.activite_titre] = s.activite_description || '';
    });

    const modalHTML = `
        <div class="modal fade" id="sessionSelectionModal" tabindex="-1" role="dialog" aria-labelledby="inscriptionModalLabel" aria-hidden="true">
            <div class="modal-dialog modal-dialog-centered modal-lg">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title" id="inscriptionModalLabel">Inscription — ${eventData.title}</h5>
                        <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Fermer"></button>
                    </div>
                    <div class="modal-body">
                        <div class="inscription-steps mb-4">
                            <div class="row text-center">
                                <div class="col-4 step-item active" id="step-session">
                                    <div class="step-number">1</div>
                                    <div class="step-label">Session</div>
                                </div>
                                <div class="col-4 step-item" id="step-form">
                                    <div class="step-number">2</div>
                                    <div class="step-label">Formulaire</div>
                                </div>
                                <div class="col-4 step-item" id="step-summary">
                                    <div class="step-number">3</div>
                                    <div class="step-label">Récapitulatif</div>
                                </div>
                            </div>
                            <div class="progress mt-2">
                                <div class="progress-bar" role="progressbar" style="width: 33%" aria-valuenow="33" aria-valuemin="0" aria-valuemax="100"></div>
                            </div>
                        </div>

                        <div id="session-selection-step">
                            <div class="animation-info mb-3"><h5>${eventData.title}</h5></div>
                            ${availableSessions.length > 0 ? `
                                <div class="sessions-container">
                                    ${[...new Set(availableSessions.map(s => s.activite_titre))].filter(Boolean).length > 0 ? `
                                    <div class="row mb-3">
                                        <div class="col-12">
                                            <select class="form-select" id="activity-filter">
                                                <option value="">Toutes les activités</option>
                                                ${[...new Set(availableSessions.map(s => s.activite_titre))].filter(Boolean).map(a => `<option value="${a}">${a}</option>`).join('')}
                                            </select>
                                            <div id="activite-description-box" class="mt-2 p-2 bg-light rounded small text-muted" style="display:none;"></div>
                                        </div>
                                    </div>` : ''}
                                    <h6>Sélectionnez une session pour vous inscrire :</h6>
                                    <div class="sessions-list list-group mt-3"></div>
                                    <div class="sessions-pagination mt-3 d-flex justify-content-between align-items-center">
                                        <small class="text-muted sessions-page-info"></small>
                                        <nav aria-label="Navigation des sessions">
                                            <ul class="pagination pagination-sm mb-0">
                                                <li class="page-item sessions-prev">
                                                    <button class="page-link" type="button">&lsaquo; Pr&eacute;c&eacute;dent</button>
                                                </li>
                                                <li class="page-item sessions-next">
                                                    <button class="page-link" type="button">Suivant &rsaquo;</button>
                                                </li>
                                            </ul>
                                        </nav>
                                    </div>
                                </div>
                            ` : `
                                <div class="no-sessions alert alert-warning">
                                    Aucune session avec des places disponibles pour cette animation.
                                </div>
                            `}
                        </div>

                        <div id="inscription-form-step" style="display:none;">
                            <div class="session-details card mb-4">
                                <div class="card-body">
                                    <h5 class="card-title">Détails de la session</h5>
                                    <p class="session-date mb-1"><i class="bi bi-calendar"></i> <span></span></p>
                                    <p class="session-horaire mb-1"><i class="bi bi-clock"></i> <span></span></p>
                                    <p class="session-lieu mb-1"><i class="bi bi-geo-alt"></i> <span></span></p>
                                    <p class="session-places mb-1"><i class="bi bi-people"></i></p>
                                    <p class="session-max-inscriptions mb-1"></p>
                                </div>
                            </div>
                            <form id="go-booking-inscription-form" class="needs-validation" novalidate>
                                <input type="hidden" name="animation_id" id="animation_id" value="${eventData.id}">
                                <input type="hidden" name="session_id" id="session_id" value="">
                                <h5 class="mb-3">Participant principal</h5>
                                <div class="row g-3">
                                    <div class="col-md-6">
                                        <label for="nom" class="form-label">Nom *</label>
                                        <input type="text" class="form-control" id="nom" name="nom" placeholder="Votre nom" required>
                                        <div class="invalid-feedback">Veuillez saisir votre nom</div>
                                    </div>
                                    <div class="col-md-6">
                                        <label for="prenom" class="form-label">Prénom *</label>
                                        <input type="text" class="form-control" id="prenom" name="prenom" placeholder="Votre prénom" required>
                                        <div class="invalid-feedback">Veuillez saisir votre prénom</div>
                                    </div>
                                    <div class="col-md-6">
                                        <label for="email" class="form-label">Email *</label>
                                        <input type="email" class="form-control" id="email" name="email" placeholder="exemple@email.com" required>
                                        <div class="invalid-feedback">Veuillez saisir une adresse email valide</div>
                                    </div>
                                    <div class="col-md-6">
                                        <label for="telephone" class="form-label">Téléphone *</label>
                                        <input type="tel" class="form-control" id="telephone" name="telephone" placeholder="0612345678 (10 chiffres)" pattern="0[1-9][0-9]{8}" required>
                                        <div class="invalid-feedback">Le numéro doit contenir 10 chiffres et commencer par 0</div>
                                    </div>
                                    <div class="col-md-6" id="taille_cm_principal_wrapper" style="display:none;">
                                        <label for="taille_cm" class="form-label">Taille (cm) *</label>
                                        <input type="number" class="form-control" id="taille_cm" name="taille_cm" min="50" max="250" placeholder="ex\u00a0: 175">
                                        <div class="invalid-feedback">Veuillez indiquer votre taille en cm</div>
                                    </div>
                                </div>
                                <div class="participants-supplementaires mt-4">
                                    <h5 class="mb-3">Participants supplémentaires</h5>
                                    <div class="participants-list"></div>
                                    <button type="button" class="btn btn-outline-primary w-100 mt-3 add-participant-btn">
                                        <i class="bi bi-plus-circle"></i> Ajouter un participant
                                    </button>
                                </div>
                                <div class="form-check mt-4">
                                    <input class="form-check-input" type="checkbox" value="1" id="accept_terms" name="accept_terms" required>
                                    <label class="form-check-label" for="accept_terms">
                                        J'autorise les équipes du SMMAG à conserver mes données personnelles transmises par le présent formulaire. Conformément à la RGPD, les informations recueillies sont enregistrées dans un fichier informatique par le SMMAG pour archiver les demandes d'inscription aux animations. La base légale du traitement est celle du consentement. Les données collectées seront communiquées aux seules équipes du SMMAG en charge des animations. Voir notre <a href="/politique-de-gestion-des-cookies/" target="_blank">politique de gestion des cookies</a>. *
                                    </label>
                                    <div class="invalid-feedback">Vous devez accepter les conditions générales pour continuer</div>
                                </div>
                                <div class="form-check mt-3">
                                    <input class="form-check-input" type="checkbox" value="1" id="newsletter_optin" name="newsletter_optin">
                                    <label class="form-check-label" for="newsletter_optin">
                                        J'accepte de recevoir la Newsletter FaitesDuVélo. Votre adresse électronique est indispensable pour vous envoyer la Newsletter, vos données sont collectées par le SMMAG, sur la base de votre consentement. Vous pourrez vous désinscrire à tout moment via le bouton dédié dans la newsletter ou en adressant votre demande de désinscription par mail à faitesduvelo@smmag.fr. Pour plus d'informations concernant vos données personnelles et vos droits, vous pouvez consulter notre <a href="/politique-de-confidentialite/" target="_blank">politique de confidentialité</a>.
                                    </label>
                                </div>
                            </form>
                        </div>

                        <div id="summary-step" style="display:none;">
                            <div class="summary-content"></div>
                        </div>
                    </div>
                    <div class="modal-footer">
                        <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Annuler</button>
                        <button type="button" class="btn btn-outline-secondary back-btn" style="display:none;">Retour</button>
                        <button type="button" class="btn btn-primary next-btn">Continuer</button>
                        <button type="button" class="btn btn-success submit-inscription-btn" style="display:none;">Confirmer l'inscription</button>
                    </div>
                </div>
            </div>
        </div>
    `;

    if (!document.getElementById('multi-step-modal-styles')) {
        const s = document.createElement('style');
        s.id = 'multi-step-modal-styles';
        s.textContent = [
            '.inscription-steps .step-item{position:relative;padding:15px 5px}',
            '.inscription-steps .step-number{width:30px;height:30px;border-radius:50%;background-color:#e9ecef;color:#6c757d;display:flex;align-items:center;justify-content:center;margin:0 auto 8px;font-weight:bold}',
            '.inscription-steps .step-item.active .step-number{background-color:#0d6efd;color:white}',
            '.inscription-steps .step-label{font-size:.9rem}',
            '.session-details{background-color:#f8f9fa;border-left:4px solid #0d6efd}',
            '.list-group-item.selected{border-color:#0d6efd;background-color:rgba(13,110,253,.1)}',
            '.participant-group.card{border:1px solid #dee2e6;border-radius:.375rem;overflow:hidden;margin-bottom:1rem;box-shadow:0 .125rem .25rem rgba(0,0,0,.075)}',
            '.participant-group .card-header{background-color:#f8f9fa;border-bottom:1px solid #dee2e6;padding:.75rem 1rem}',
            '.participant-group .card-body{padding:1rem}',
            '.remove-participant-btn{cursor:pointer;transition:all .2s}.remove-participant-btn:hover{opacity:.7}',
            '.add-participant-btn{transition:all .3s}.add-participant-btn:hover{background-color:#0d6efd;color:white}',
            '#activity-filter{border-radius:.375rem;border:1px solid #dee2e6;padding:.5rem;background-color:#fff;transition:border-color .15s ease-in-out,box-shadow .15s ease-in-out}',
            '#activity-filter:focus{border-color:#86b7fe;box-shadow:0 0 0 .25rem rgba(13,110,253,.25)}',
            '.sessions-container{background-color:#f8f9fa;padding:1.5rem;border-radius:.5rem;margin-bottom:1.5rem}',
            '#summary-step .card{border-left:4px solid #0d6efd}',
            '#summary-step .card-header{background-color:#f8f9fa;font-size:.95rem}',
            '.inscription-steps .step-item.completed .step-number{background-color:#198754;color:white}',
        ].join('');
        document.head.appendChild(s);
    }

    document.body.insertAdjacentHTML('beforeend', modalHTML);
    const modal = document.getElementById('sessionSelectionModal');
    if (!modal) return;

    let selectedSessionId   = null;
    let selectedSessionData = null;

    const sessionStep          = modal.querySelector('#session-selection-step');
    const formStep             = modal.querySelector('#inscription-form-step');
    const nextBtn              = modal.querySelector('.next-btn');
    const backBtn              = modal.querySelector('.back-btn');
    const submitBtn            = modal.querySelector('.submit-inscription-btn');
    const progressBar          = modal.querySelector('.progress-bar');
    const sessionStepIndicator = modal.querySelector('#step-session');
    const formStepIndicator    = modal.querySelector('#step-form');
    const summaryStepIndicator = modal.querySelector('#step-summary');
    const summaryStep          = modal.querySelector('#summary-step');
    // Mise à jour des détails session (affichés à l'étape 2)
    const updateSessionDetails = (sd) => {
        document.getElementById('session_id').value = sd.id;
        modal.querySelector('.session-date span').textContent    = sd.date;
        const formattedHoraire = (sd.horaire || '').replace(/\b(\d{2}:\d{2}):\d{2}\b/g, '$1');
        modal.querySelector('.session-horaire span').textContent = formattedHoraire;
        const lieuText = sd.ville ? `${sd.lieu}, ${sd.ville}` : sd.lieu;
        modal.querySelector('.session-lieu span').textContent    = lieuText;
        const places = parseInt(sd.places);
        modal.querySelector('.session-places').innerHTML = `
            <i class="bi bi-people"></i>
            <span class="badge ${places > 0 || places === -1 ? 'bg-success' : 'bg-danger'} ms-2">
                ${places === -1 ? 'Places illimitées' : `${places} place${places > 1 ? 's' : ''} disponible${places > 1 ? 's' : ''}`}
            </span>`;
        const maxInsc = parseInt(sd.max_inscriptions_user) || 1;
        modal.querySelector('.session-max-inscriptions').innerHTML = `
            <i class="bi bi-person-plus"></i> <span>${maxInsc} participant${maxInsc > 1 ? 's' : ''} max. par inscription</span>`;
        const cardBody = modal.querySelector('.session-details .card-body');
        cardBody.querySelector('.session-activity')?.remove();
        if (sd.has_activite && sd.activite_titre) {
            const div = document.createElement('div');
            div.className = 'session-activity mt-2';
            div.innerHTML = `<p class="mb-1"><strong><i class="bi bi-info-circle"></i> Activité :</strong> ${sd.activite_titre}</p>
                ${sd.activite_description ? `<p class="small text-muted">${sd.activite_description}</p>` : ''}`;
            cardBody.appendChild(div);
        }
    };

    let currentStep = 1;

    const goToFormStep = () => {
        currentStep = 2;
        sessionStep.style.display = 'none';
        formStep.style.display    = 'block';
        summaryStep.style.display = 'none';
        nextBtn.style.display     = 'inline-block';
        nextBtn.disabled          = false;
        backBtn.style.display     = 'inline-block';
        submitBtn.style.display   = 'none';
        progressBar.style.width   = '66%';
        sessionStepIndicator.classList.remove('active');
        sessionStepIndicator.classList.add('completed');
        summaryStepIndicator.classList.remove('active', 'completed');
        formStepIndicator.classList.add('active');
        formStepIndicator.classList.remove('completed');
        updateSessionDetails(selectedSessionData);
        if (modal._participantManager && selectedSessionData?.max_inscriptions_user) {
            const max = parseInt(selectedSessionData.max_inscriptions_user);
            if (!isNaN(max) && max > 0) modal._participantManager.setMaxParticipants(max - 1);
        }
    };

    const goToSessionStep = () => {
        currentStep = 1;
        formStep.style.display    = 'none';
        summaryStep.style.display = 'none';
        sessionStep.style.display = 'block';
        nextBtn.style.display     = 'inline-block';
        nextBtn.disabled          = !selectedSessionId;
        backBtn.style.display     = 'none';
        submitBtn.style.display   = 'none';
        progressBar.style.width   = '33%';
        formStepIndicator.classList.remove('active', 'completed');
        summaryStepIndicator.classList.remove('active', 'completed');
        sessionStepIndicator.classList.remove('completed');
        sessionStepIndicator.classList.add('active');
        modal._participantManager?.resetParticipants();
    };

    const goToSummaryStep = () => {
        const form = modal.querySelector('#go-booking-inscription-form');
        if (!form) return;
        form.classList.add('was-validated');
        if (!form.checkValidity()) {
            form.querySelector(':invalid')?.focus();
            return;
        }

        currentStep = 3;
        formStep.style.display    = 'none';
        sessionStep.style.display = 'none';
        summaryStep.style.display = 'block';
        nextBtn.style.display     = 'none';
        backBtn.style.display     = 'inline-block';
        submitBtn.style.display   = 'inline-block';
        progressBar.style.width   = '100%';
        sessionStepIndicator.classList.remove('active');
        sessionStepIndicator.classList.add('completed');
        formStepIndicator.classList.remove('active');
        formStepIndicator.classList.add('completed');
        summaryStepIndicator.classList.add('active');

        // Build summary content
        const sd        = selectedSessionData;
        const nom       = form.querySelector('#nom').value;
        const prenom    = form.querySelector('#prenom').value;
        const email     = form.querySelector('#email').value;
        const telephone = form.querySelector('#telephone').value;
        const taille    = form.querySelector('#taille_cm').value;

        let html = `
            <div class="card mb-3">
                <div class="card-header"><strong><i class="bi bi-calendar-event"></i> Session sélectionnée</strong></div>
                <div class="card-body">
                    <p class="mb-1"><i class="bi bi-calendar"></i> ${sd.date}${sd.day ? ' (' + sd.day + ')' : ''}</p>
                    <p class="mb-1"><i class="bi bi-clock"></i> ${sd.horaire}</p>
                    <p class="mb-1"><i class="bi bi-geo-alt"></i> ${sd.ville ? sd.lieu + ', ' + sd.ville : sd.lieu}</p>
                    <p class="mb-1"><i class="bi bi-person-plus"></i> ${parseInt(sd.max_inscriptions_user) || 1} participant${(parseInt(sd.max_inscriptions_user) || 1) > 1 ? 's' : ''} max. par inscription</p>
                    ${sd.has_activite && sd.activite_titre ? `<p class="mb-0"><i class="bi bi-info-circle"></i> Activité : ${sd.activite_titre}</p>` : ''}
                </div>
            </div>
            <div class="card mb-3">
                <div class="card-header"><strong><i class="bi bi-person"></i> Participant principal</strong></div>
                <div class="card-body">
                    <p class="mb-1"><strong>Nom :</strong> ${nom}</p>
                    <p class="mb-1"><strong>Prénom :</strong> ${prenom}</p>
                    <p class="mb-1"><strong>Email :</strong> ${email}</p>
                    <p class="mb-1"><strong>Téléphone :</strong> ${telephone}</p>
                    ${taille ? `<p class="mb-0"><strong>Taille :</strong> ${taille} cm</p>` : ''}
                </div>
            </div>`;

        const extraParticipants = form.querySelectorAll('.participant-group');
        if (extraParticipants.length > 0) {
            html += `<div class="card mb-3"><div class="card-header"><strong><i class="bi bi-people"></i> Participants supplémentaires (${extraParticipants.length})</strong></div><div class="card-body">`;
            extraParticipants.forEach((group, index) => {
                const pNom    = group.querySelector('[name$="[nom]"]')?.value || '';
                const pPrenom = group.querySelector('[name$="[prenom]"]')?.value || '';
                const pEmail  = group.querySelector('[name$="[email]"]')?.value || '';
                const pTel    = group.querySelector('[name$="[telephone]"]')?.value || '';
                const pTaille = group.querySelector('[name$="[taille_cm]"]')?.value || '';
                html += `
                    <div class="${index > 0 ? 'border-top pt-2 mt-2' : ''}">
                        <p class="mb-1 fw-semibold">Participant ${index + 2}</p>
                        <p class="mb-1"><strong>Nom :</strong> ${pNom} — <strong>Prénom :</strong> ${pPrenom}</p>
                        <p class="mb-1"><strong>Email :</strong> ${pEmail}</p>
                        <p class="mb-1"><strong>Téléphone :</strong> ${pTel}</p>
                        ${pTaille ? `<p class="mb-0"><strong>Taille :</strong> ${pTaille} cm</p>` : ''}
                    </div>`;
            });
            html += `</div></div>`;
        }

        html += `<div class="alert alert-info small mb-0"><i class="bi bi-info-circle"></i> Veuillez vérifier les informations ci-dessus avant de confirmer votre inscription.</div>`;

        modal.querySelector('.summary-content').innerHTML = html;
    };

    // ── Pagination des sessions ─────────────────────────────────────────────
    const pager = initSessionPagination(
        modal,
        availableSessions,
        (session) => {
            if (!session) {
                selectedSessionId   = null;
                selectedSessionData = null;
                nextBtn.disabled    = true;
                return;
            }
            selectedSessionId   = String(session.id);
            selectedSessionData = {
                id                   : String(session.id),
                date                 : session.formatted_date || '',
                day                  : session.day_name || '',
                horaire              : session.horaire || `${session.heure_debut || ''} - ${session.heure_fin || ''}`,
                lieu                 : session.lieu_depart || 'Lieu non spécifié',
                ville                : session.ville || '',
                places               : session.places_disponibles,
                max_inscriptions_user: session.max_inscriptions_user,
                has_activite         : !!session.has_activite,
                activite_titre       : session.activite_titre || '',
                activite_description : session.activite_description || '',
            };
            nextBtn.disabled = false;
        },
        10,
        eventData.extendedProps?.max_inscriptions_user || 2
    );

    // Filtre activité
    const activityFilter = modal.querySelector('#activity-filter');
    if (activityFilter) {
        activityFilter.addEventListener('change', (e) => {
            const selected = e.target.value;
            pager.setFilter(selected);
            const descBox = modal.querySelector('#activite-description-box');
            if (descBox) {
                const desc = selected ? (activiteMap[selected] || '') : '';
                descBox.textContent   = desc;
                descBox.style.display = desc ? '' : 'none';
            }
        });
    }

    // Auto-sélection de l'activité si une seule est disponible
    const uniqueActivities = [...new Set(availableSessions.map(s => s.activite_titre).filter(Boolean))];
    if (activityFilter && uniqueActivities.length === 1) {
        const singleActivity = uniqueActivities[0];
        activityFilter.value = singleActivity;
        pager.setFilter(singleActivity);
        const descBox = modal.querySelector('#activite-description-box');
        if (descBox) {
            const desc = activiteMap[singleActivity] || '';
            descBox.textContent   = desc;
            descBox.style.display = desc ? '' : 'none';
        }
    }

    // Auto-sélection si exactement une session est visible
    if (!pager.autoSelectIfSingle()) {
        nextBtn.disabled = true;
    }

    nextBtn.addEventListener('click', () => {
        if (currentStep === 1) {
            if (selectedSessionId) goToFormStep();
            else alert('Veuillez sélectionner une session pour continuer.');
        } else if (currentStep === 2) {
            goToSummaryStep();
        }
    });
    backBtn.addEventListener('click', () => {
        if (currentStep === 3) goToFormStep();
        else if (currentStep === 2) goToSessionStep();
    });

    // Soumission du formulaire
    submitBtn.addEventListener('click', async () => {
        const form = modal.querySelector('#go-booking-inscription-form');
        if (!form) return;
        form.classList.add('was-validated');
        if (modal._participantManager) {
            if (!form.checkValidity()) {
                form.querySelector(':invalid')?.focus();
                return;
            }
            form.dataset.eventId = eventData.id;
            const success = await modal._participantManager.handleSubmit(form);
            if (success) {
                bootstrap.Modal.getInstance(modal)?.hide();
                showNotification('success', 'Votre inscription a été enregistrée avec succès !');
                setTimeout(() => window.location.reload(), 1000);
            }
            return;
        }
        if (!form.checkValidity()) {
            form.querySelector(':invalid')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            return;
        }
        try {
            submitBtn.disabled  = true;
            submitBtn.innerHTML = '<span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span> Traitement…';
            const fd  = new FormData(form);
            const res = await fetch(window.goBookingAgenda.ajax_url, { method: 'POST', body: fd });
            const result = await res.json();
            if (result.success) {
                bootstrap.Modal.getInstance(modal)?.hide();
                alert('Votre inscription a été enregistrée avec succès !');
                setTimeout(() => window.location.reload(), 1000);
            } else {
                alert('Erreur : ' + (result.data?.message || 'Une erreur est survenue.'));
                submitBtn.disabled  = false;
                submitBtn.innerHTML = "Confirmer l'inscription";
            }
        } catch (err) {
            console.error(err);
            alert('Une erreur est survenue. Veuillez réessayer.');
            submitBtn.disabled  = false;
            submitBtn.innerHTML = "Confirmer l'inscription";
        }
    });

    modal.addEventListener('hidden.bs.modal', cleanupExistingModals);

    const bsModal = new bootstrap.Modal(modal);
    bsModal.show();
    setupInscriptionParticipants(modal, eventData);
}

/**
 * Initialise ParticipantManager dans la modale d'inscription.
 */
function setupInscriptionParticipants(modal, eventData) {
    const form = modal.querySelector('#go-booking-inscription-form');
    if (!form) return;
    // Clone du bouton pour supprimer les anciens écouteurs
    const addBtn = modal.querySelector('.add-participant-btn');
    if (addBtn) {
        const newBtn = addBtn.cloneNode(true);
        addBtn.parentNode.replaceChild(newBtn, addBtn);
    }
    try {
        const manager       = new ParticipantManager(form);
        const maxInscriptions = parseInt(eventData.extendedProps?.max_inscriptions_user) || 2;
        manager.setMaxParticipants(maxInscriptions - 1);
        const requireTaille = eventData.extendedProps?.require_taille_cm === true;
        manager.setRequireTailleCm(requireTaille);
        const tailleWrapper = modal.querySelector('#taille_cm_principal_wrapper');
        const tailleInput   = modal.querySelector('#taille_cm');
        if (tailleWrapper) tailleWrapper.style.display = requireTaille ? '' : 'none';
        if (tailleInput) tailleInput.required = requireTaille;
        modal._participantManager = manager;
    } catch (err) {
        console.error('Erreur ParticipantManager :', err);
    }
}
